/**
 * Custom Hook for managing complex calendar interactions (Drag & Drop, Move, Resize).
 * Orchestrates specialized hooks for Timeline and Month views.
 */
import { useEffect, useRef } from 'react';
import { toLocalISO, snapTo15Min, toDateKey } from '../../../../../utils/dateUtils';
import { useTimelineInteractions } from './useTimelineInteractions';
import { useMonthInteractions } from './useMonthInteractions';
import { useDropHandlers } from './useDropHandlers';
import { getTimedLayout } from './interactionHelpers';

export function useCalendarInteractions({
    interaction,
    setInteraction,
    interactionEnded,
    setInteractionEnded,
    assignments,
    onAssignmentUpdate,
    onDropWorker,
    scrollRef,
    actualAlldayWidth,
    dynamicHourWidth,
    hourStart,
    GRID_BUFFER,
    externalDraggingAssignment
}) {
    const interactionRef = useRef(interaction);
    useEffect(() => {
        interactionRef.current = interaction;
    }, [interaction]);

    // 1. Timeline-specific interactions
    const { calculateInteractionState, handleInteractionStart } = useTimelineInteractions({
        scrollRef, actualAlldayWidth, dynamicHourWidth, hourStart, GRID_BUFFER, assignments, setInteraction
    });

    // 2. Month-specific interactions
    const { handleMonthDragStart, handleMonthDragEnd, getMonthDragMeta } = useMonthInteractions({
        interaction, setInteraction
    });

    // 3. Drop handling
    const { handleDrop, handleDropAllDay, buildTimedReorderUpdates } = useDropHandlers({
        interaction, setInteraction, assignments, onAssignmentUpdate, onDropWorker,
        hourStart, dynamicHourWidth, GRID_BUFFER
    });

    // 4. Global Event Listeners
    useEffect(() => {
        const handleMouseMove = (e) => {
            const currentInteraction = interactionRef.current;
            if (!currentInteraction) return;
            const newState = calculateInteractionState(e, currentInteraction);
            if (newState) {
                setInteraction(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        currentX: e.clientX,
                        currentY: e.clientY,
                        currentS: newState.finalS ?? prev.currentS,
                        currentE: newState.finalE ?? prev.currentE,
                        currentDay: newState.activeDay ?? prev.currentDay,
                        visualTopOffset: newState.visualTopOffset ?? prev.visualTopOffset,
                        targetLane: newState.targetLane ?? prev.targetLane,
                        isOverAllDay: newState.isOverAllDay,
                        moved: prev.moved || newState.moved
                    };
                });
            } else {
                setInteraction(prev => prev ? { ...prev, currentS: null, currentE: null } : null);
            }
        };

        const handleMouseUp = (e) => {
            const currentInteraction = interactionRef.current;
            if (!currentInteraction) return;

            const finalState = calculateInteractionState(e, currentInteraction);
            if (finalState) {
                if (['resize-top', 'resize-bottom'].includes(currentInteraction.type)) {
                    if (currentInteraction.moved || (finalState && finalState.moved)) {
                        const fS = finalState.finalS || new Date(currentInteraction.origS);
                        const fE = finalState.finalE || new Date(currentInteraction.origE);
                        fS.setHours(0,0,0,0);
                        fE.setHours(0,0,0,0);
                        onAssignmentUpdate([{
                            ...currentInteraction.assignment,
                            start_datetime: toLocalISO(fS),
                            end_datetime: toLocalISO(fE)
                        }]);
                    }
                    setInteraction(null);
                    return;
                }
                
                const isAllDayDrop = finalState.isOverAllDay;
                let finalS = finalState.finalS;
                let finalE = finalState.finalE;

                if (isAllDayDrop) {
                    finalS.setHours(0, 0, 0, 0);
                    finalE.setHours(0, 0, 0, 0);
                } else if (currentInteraction.type === 'move-allday') {
                    if (finalS.getHours() === 0 && finalS.getMinutes() === 0 && finalE.getHours() === 0 && finalE.getMinutes() === 0) {
                         finalE = new Date(finalS.getTime() + 60 * 60 * 1000);
                    }
                }
                
                const updates = [];
                if ((currentInteraction.type === 'move' || currentInteraction.type === 'move-allday') && !isAllDayDrop) {
                    updates.push(...buildTimedReorderUpdates(
                        currentInteraction.assignment,
                        finalS,
                        finalE,
                        toDateKey(finalState.activeDay || new Date(currentInteraction.origS)),
                        { ...currentInteraction, targetLane: finalState.targetLane }
                    ));
                } else {
                    updates.push({
                        ...currentInteraction.assignment,
                        start_datetime: toLocalISO(finalS),
                        end_datetime: toLocalISO(finalE),
                        all_day: isAllDayDrop,
                        is_all_day: isAllDayDrop,
                        stack_order: currentInteraction.assignment.stack_order ?? 0
                    });
                }

                if (updates.length > 0 && (currentInteraction.moved || (finalState && finalState.moved))) {
                    onAssignmentUpdate(updates);
                }
            }
            
            if (currentInteraction && (currentInteraction.moved || (finalState && finalState.moved))) {
                setInteractionEnded(true);
                setTimeout(() => setInteractionEnded(false), 200);
            }
            setInteraction(null);
        };

        const handleDragEnd = () => setInteraction(null);

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('dragend', handleDragEnd);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('dragend', handleDragEnd);
        };
    }, [calculateInteractionState, onAssignmentUpdate, setInteraction, setInteractionEnded, buildTimedReorderUpdates]);

    // 5. Shared Handlers
    const handleDragOver = (e, monthDay = null) => {
        e.preventDefault();
        e.currentTarget.classList.add('drag-over');

        if (monthDay) {
            const dragPayload = (() => {
                try { return JSON.parse(e.dataTransfer?.getData('application/json') || '{}'); } catch { return {}; }
            })();
            const draggedAssignment = interaction?.assignment || getMonthDragMeta()?.assignment || dragPayload.assignment;
            if (draggedAssignment) {
                const sourceDay = interaction?.sourceDay || getMonthDragMeta()?.originDay || dragPayload.originDay || draggedAssignment.start_datetime;
                setInteraction(prev => ({
                    ...(prev || {}),
                    type: 'month-move',
                    assignment: draggedAssignment,
                    sourceDay,
                    targetDay: monthDay,
                    currentX: e.clientX,
                    currentY: e.clientY,
                    moved: true
                }));
            }
            return;
        }

        if (externalDraggingAssignment && (!interaction || interaction.isExternal)) {
            const scrollContainer = scrollRef.current;
            if (!scrollContainer) return;
            const containerRect = scrollContainer.getBoundingClientRect();
            const stickyWidth = 80 + actualAlldayWidth;
            const isOverAllDay = (e.clientX - containerRect.left) < stickyWidth;

            const elements = document.elementsFromPoint(e.clientX, e.clientY);
            let row = elements.find(el => el.classList.contains('timeline-day-row'));
            if (!row) {
                 const rows = Array.from(document.querySelectorAll('.timeline-day-row'));
                 if (rows.length > 0) {
                      row = rows.reduce((prev, curr) => {
                          const pRect = prev.getBoundingClientRect();
                          const cRect = curr.getBoundingClientRect();
                          return Math.abs(cRect.top + (cRect.height/2) - e.clientY) < Math.abs(pRect.top + (pRect.height/2) - e.clientY) ? curr : prev;
                      });
                 }
            }
            if (!row) return;

            const activeDay = new Date(row.getAttribute('data-day') || new Date());
            activeDay.setHours(0,0,0,0);

            const gridX = (e.clientX - containerRect.left - stickyWidth) + scrollContainer.scrollLeft - GRID_BUFFER;
            const dropTime = new Date(activeDay);
            dropTime.setHours(0, Math.round((hourStart + Math.max(0, gridX / dynamicHourWidth)) * 60), 0, 0);
            const snappedMouseTime = snapTo15Min(dropTime);

            let finalS = isOverAllDay ? new Date(activeDay) : snappedMouseTime;
            let finalE = isOverAllDay ? new Date(activeDay) : new Date(finalS.getTime() + 60 * 60 * 1000);
            if (isOverAllDay) { finalS.setHours(0,0,0,0); finalE.setHours(0,0,0,0); }

            setInteraction({
                type: isOverAllDay ? 'move-allday' : 'move',
                assignment: externalDraggingAssignment,
                startX: e.clientX, startY: e.clientY,
                currentX: e.clientX, currentY: e.clientY,
                origS: finalS.toISOString(), origE: finalE.toISOString(),
                currentS: finalS, currentE: finalE,
                currentDay: activeDay,
                isOverAllDay, moved: true, isExternal: true, isOverSidebar: false
            });
        } 
        else if (interactionRef.current) {
            const newState = calculateInteractionState(e, interactionRef.current);
            if (newState) {
                setInteraction(prev => prev ? {
                    ...prev,
                    currentX: e.clientX, currentY: e.clientY,
                    currentS: newState.finalS ?? prev.currentS,
                    currentE: newState.finalE ?? prev.currentE,
                    currentDay: newState.activeDay ?? prev.currentDay,
                    visualTopOffset: newState.visualTopOffset ?? prev.visualTopOffset,
                    targetLane: newState.targetLane ?? prev.targetLane,
                    isOverAllDay: newState.isOverAllDay,
                    moved: prev.moved || newState.moved
                } : null);
            } else {
                setInteraction(prev => prev ? { ...prev, currentS: null, currentE: null } : null);
            }
        }
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('drag-over');
        if (externalDraggingAssignment && interactionRef.current?.isExternal) setInteraction(null);
    };

    return {
        handleInteractionStart,
        getTimedLayout,
        handleDrop,
        handleDropAllDay,
        handleDragOver,
        handleDragLeave,
        handleMonthDragStart,
        handleMonthDragEnd
    };
}
